<template>
    <v-card flat class="pa-2">

        <v-layout row wrap align-center class="pb-1">
            <v-flex shrink class="pr-2">
                <span class="caption greyType--text">Map Columns To Fields</span>
            </v-flex>
            <v-flex shrink>
                <v-chip small>{{ run.row_count }} rows</v-chip>
            </v-flex>
        </v-layout>

        <!--Column Mapping-->
        <v-radio-group v-model="folderNameColumn" hide-details class="pa-0 ma-0">
            <v-layout row align-center class="pb-1">
                <v-flex shrink style="width: 60px" class="text-xs-center">
                    <span class="caption greyType--text">Folder Name</span>
                </v-flex>
                <v-flex xs4 class="pl-2">
                    <span class="caption greyType--text">Column</span>
                </v-flex>
                <v-flex xs4 class="pl-2">
                    <span class="caption greyType--text">Field Set</span>
                </v-flex>
                <v-flex xs4 class="pl-2">
                    <span class="caption greyType--text">Field</span>
                </v-flex>
            </v-layout>
            <v-divider></v-divider>
            <template v-for="(row, i) in mapping">
                <v-layout row align-center :key="'mapRow' + i" class="py-0">
                    <v-flex shrink style="width: 60px" class="text-xs-center">
                        <v-tooltip right>
                            <v-radio
                                    slot="activator"
                                    :value="row.column"
                                    color="primary"
                                    hide-details
                                    class="ma-0 pa-0"
                            ></v-radio>
                            <span>Use This Column As The Folder Name</span>
                        </v-tooltip>
                    </v-flex>
                    <v-flex xs4 class="pl-2">
                        <span class="body-1">{{ row.column }}</span>
                    </v-flex>
                    <v-flex xs4 class="pl-2">
                        <v-autocomplete
                                dense
                                :value="row.fieldSet"
                                :items="fieldSets"
                                label="Field Set"
                                item-text="description"
                                item-value="id"
                                hide-details
                                single-line
                                color="primary"
                                class="ma-0 pa-0"
                                clearable
                                @input="setRowFieldSet(i, $event)"
                        ></v-autocomplete>
                    </v-flex>
                    <v-flex xs4 class="pl-2">
                        <v-autocomplete
                                dense
                                :value="row.field"
                                :items="fieldsForSet(row.fieldSet)"
                                label="Field"
                                item-text="description"
                                item-value="id"
                                hide-details
                                single-line
                                color="primary"
                                class="ma-0 pa-0"
                                clearable
                                :disabled="row.fieldSet == null"
                                @input="setRowField(i, $event)"
                        ></v-autocomplete>
                    </v-flex>
                </v-layout>
                <v-divider :key="'mapDivider' + i"></v-divider>
            </template>
        </v-radio-group>

        <!--Folder Groups-->
        <div class="pt-2 pb-1">
            <span class="caption greyType--text">Folder Groups</span>
        </div>
        <v-layout row>
            <v-flex xs12 sm6>
                <v-autocomplete
                        dense
                        v-model="selectedFolderGroups"
                        :items="folderGroups"
                        label="Select Folder Groups"
                        item-text="description"
                        item-value="id"
                        multiple
                        hide-details
                        single-line
                        color="primary"
                        class="ma-0 pa-0"
                        clearable
                        chips
                        small-chips
                        deletable-chips
                ></v-autocomplete>
            </v-flex>
        </v-layout>

        <!--Strategy-->
        <div class="pt-2 pb-1">
            <span class="caption greyType--text">Matching Strategy</span>
        </div>
        <v-layout row>
            <v-flex xs12 sm6>
                <v-select
                        dense
                        v-model="strategy"
                        :items="strategyItems"
                        label="Strategy"
                        hide-details
                        single-line
                        color="primary"
                        class="ma-0 pa-0"
                ></v-select>
            </v-flex>
        </v-layout>

        <!--Conflict Mode-->
        <div class="pt-2 pb-1">
            <span class="caption greyType--text">When A Merged Folder Already Has A Value</span>
        </div>
        <v-btn-toggle v-model="mergeConflictMode" mandatory class="elevation-1">
            <v-btn small flat value="fill_empty" color="primary">Fill Empty Values Only</v-btn>
            <v-btn small flat value="overwrite" color="primary">Overwrite Existing Values</v-btn>
        </v-btn-toggle>
        <v-card flat class="mt-1 pa-2" color="grey lighten-4">
            <span class="caption greyType--text" v-if="mergeConflictMode == 'fill_empty'">
                Existing values on a folder are never changed. Values the import could not write are listed on the Errors sheet of the result workbook.
            </span>
            <span class="caption greyType--text" v-else>
                Import values replace what is currently on the folder. Every replaced value is listed on the Errors sheet of the result workbook.
            </span>
        </v-card>

        <!--Save To Template-->
        <v-checkbox
                v-model="saveToTemplate"
                :label="template != null ? 'Save these settings to the template' : 'Save these settings as a new template'"
                color="primary"
                hide-details
                class="pa-0 ma-0 pt-2"
        ></v-checkbox>
        <v-text-field
                v-if="saveToTemplate && template == null"
                v-model="newTemplateName"
                label="New Template Name"
                single-line
                dense
                hide-details
                color="primary"
                class="pa-0 ma-0 pt-1"
                style="max-width: 320px"
        ></v-text-field>
        <div v-if="saveToTemplate && template == null">
            <span class="caption greyType--text">The template will be created with these column mappings and settings, ready to reuse on the Upload step next time</span>
        </div>

        <!--Actions-->
        <v-layout row align-center class="pt-2">
            <v-flex shrink>
                <v-btn small flat color="grey" class="ma-0" :disabled="busy" @click="$emit('back')">
                    <v-icon small left>chevron_left</v-icon>
                    Back
                </v-btn>
            </v-flex>
            <v-flex shrink class="pl-2">
                <v-btn
                        color="secondary"
                        class="ma-0"
                        :disabled="busy"
                        :loading="busy"
                        @click="continueMapping"
                >
                    Preview Import
                    <v-icon right>chevron_right</v-icon>
                </v-btn>
            </v-flex>
        </v-layout>
    </v-card>
</template>

<script>

    import {codeBaseMixin} from "../../codeBaseMixin";

    export default {
        mixins: [codeBaseMixin],
        props: {
            run: Object,
            template: Object,
            fieldSets: {
                type: Array,
                default: () => []
            },
            folderGroups: {
                type: Array,
                default: () => []
            },
            busy: {
                type: Boolean,
                default: false
            }
        },
        data() {
            return {
                mapping: [],
                folderNameColumn: null,
                selectedFolderGroups: [],
                strategy: 'merge',
                mergeConflictMode: 'fill_empty',
                saveToTemplate: false,
                newTemplateName: null,
                strategyItems: [
                    {text: 'Always create new folders', value: 'create'},
                    {text: 'Merge into existing folders when matched', value: 'merge'},
                    {text: 'Skip rows that match existing folders', value: 'skip'}
                ]
            }
        },
        watch: {
            run: {
                handler() {
                    this.initFromRun();
                },
                immediate: true
            },
            template() {
                this.initFromRun();
            }
        },
        methods: {
            parseMaybe(value) {
                if (value == null || value == '') {
                    return null;
                }
                if (typeof value == 'string') {
                    try {
                        return JSON.parse(value);
                    } catch (e) {
                        return null;
                    }
                }
                return value;
            },

            fieldsForSet(fieldSetId) {
                if (fieldSetId == null) {
                    return [];
                }
                let matches = this.fieldSets.filter(obj => obj.id == fieldSetId);
                if (matches.length <= 0) {
                    return [];
                }
                let fields = this.parseMaybe(matches[0].fields);
                return fields != null ? fields : [];
            },

            setRowFieldSet(index, value) {
                this.mapping[index].fieldSet = value == null ? null : value;
                this.mapping[index].field = null;
            },

            setRowField(index, value) {
                this.mapping[index].field = value == null ? null : value;
            },

            initFromRun() {
                if (this.run == null) {
                    return;
                }
                let headers = this.run.headers || [];
                this.mapping = headers.map(header => {
                    return {column: header, fieldSet: null, field: null};
                });

                let settings = this.template != null ? this.parseMaybe(this.template.settings) : null;

                let runFieldMap = this.parseMaybe(this.run.field_map);
                let fresh = runFieldMap == null || runFieldMap.length <= 0;

                let fieldMap = !fresh ? runFieldMap : (settings != null ? this.parseMaybe(settings.fieldMap) || settings.fieldMap : null);
                if (fieldMap != null && Array.isArray(fieldMap)) {
                    fieldMap.forEach(entry => {
                        if (entry == null || entry.column == null) {
                            return;
                        }
                        this.mapping.forEach(row => {
                            if (row.column == entry.column) {
                                let fields = this.fieldsForSet(entry.fieldSet);
                                if (fields.filter(obj => obj.id == entry.field).length > 0) {
                                    row.fieldSet = entry.fieldSet;
                                    row.field = entry.field;
                                }
                            }
                        });
                    });
                }

                let folderNameColumn = this.run.folder_name_column != null && this.run.folder_name_column != ''
                    ? this.run.folder_name_column
                    : (settings != null ? settings.folderNameColumn : null);
                this.folderNameColumn = folderNameColumn != null && headers.includes(folderNameColumn) ? folderNameColumn : null;

                let runGroups = this.parseMaybe(this.run.folder_groups);
                let groups = runGroups != null && runGroups.length > 0
                    ? runGroups
                    : (settings != null ? this.parseMaybe(settings.folderGroups) || settings.folderGroups : null);
                this.selectedFolderGroups = groups != null && Array.isArray(groups) ? groups.slice() : [];

                if (fresh && settings != null && settings.strategy != null) {
                    this.strategy = settings.strategy;
                } else if (this.run.strategy != null) {
                    this.strategy = this.run.strategy;
                } else {
                    this.strategy = 'merge';
                }

                if (fresh && settings != null && settings.mergeConflictMode != null) {
                    this.mergeConflictMode = settings.mergeConflictMode;
                } else if (this.run.merge_conflict_mode != null) {
                    this.mergeConflictMode = this.run.merge_conflict_mode;
                } else {
                    this.mergeConflictMode = 'fill_empty';
                }

                this.saveToTemplate = false;
                this.newTemplateName = null;
            },

            continueMapping() {
                let fieldMap = this.mapping
                    .filter(row => row.fieldSet != null && row.field != null)
                    .map(row => {
                        return {column: row.column, fieldSet: row.fieldSet, field: row.field};
                    });

                if (this.folderNameColumn == null) {
                    this.showSnack('Error', 'Please mark one column as the Folder Name column', 'Close', 'red');
                    return;
                }
                if (this.selectedFolderGroups == null || this.selectedFolderGroups.length <= 0) {
                    this.showSnack('Error', 'Please select at least one Folder Group', 'Close', 'red');
                    return;
                }
                if (fieldMap.length <= 0) {
                    this.showSnack('Error', 'Please map at least one column to a field', 'Close', 'red');
                    return;
                }

                let payload = {
                    field_map: fieldMap,
                    folder_name_column: this.folderNameColumn,
                    folder_groups: this.selectedFolderGroups,
                    strategy: this.strategy,
                    merge_conflict_mode: this.mergeConflictMode
                };
                if (this.saveToTemplate) {
                    if (this.template != null) {
                        payload.saveToTemplate = true;
                    } else {
                        if (this.newTemplateName == null || this.newTemplateName.trim() == '') {
                            this.showSnack('Error', 'Please provide a name for the new template', 'Close', 'red');
                            return;
                        }
                        payload.newTemplateName = this.newTemplateName.trim();
                    }
                }
                this.$emit('save-mapping', payload);
            }
        }
    }
</script>

<style scoped>

</style>
